import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { connectWhatsApp, getWhatsAppQR, getWhatsAppStatus, disconnectWhatsApp, getWhatsAppHealth, apiMessage, type WhatsAppHealth } from '../services/api';
import { QRCodeCanvas } from 'qrcode.react';

const fmtWindow = (iso: string) =>
  new Date(iso).toLocaleString('es-CO', { weekday: 'long', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

function HealthPanel({ health }: { health: WhatsAppHealth }) {
  const capPct = Math.min(100, Math.round((health.campaignSentToday / health.campaignDailyCap) * 100));
  const windowOpen = health.nextCampaignWindow !== null && new Date(health.nextCampaignWindow).getTime() <= Date.now() + 60_000;
  const stats = [
    { label: 'Mensajes enviados hoy', value: health.sentToday },
    { label: 'Reconexiones (24 h)', value: health.reconnects24h, warn: health.reconnects24h >= 3 },
    { label: 'Antigüedad del número', value: `${health.linkedDays} ${health.linkedDays === 1 ? 'día' : 'días'}` },
  ];
  return (
    <div className="mt-6 bg-surface rounded-2xl border border-border-subtle shadow-sm p-6">
      <h2 className="font-semibold text-txt-primary mb-4">Salud del número</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {stats.map((s) => (
          <div key={s.label} className="bg-surface-elevated rounded-xl px-4 py-3">
            <p className={`text-xl font-bold ${s.warn ? 'text-orange-500' : 'text-txt-primary'}`}>{s.value}</p>
            <p className="text-xs text-txt-tertiary mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="text-txt-secondary">Campañas hoy</span>
        <span className="font-semibold text-txt-primary tabular-nums">{health.campaignSentToday} / {health.campaignDailyCap}</span>
      </div>
      <div className="h-2 rounded-full bg-surface-overlay overflow-hidden">
        <div className={`h-full rounded-full ${capPct >= 100 ? 'bg-orange-400' : 'bg-green-500'}`} style={{ width: `${capPct}%` }} />
      </div>
      <p className="text-xs text-txt-tertiary mt-2">
        El tope diario de campañas crece con la antigüedad del número y baja si WhatsApp da avisos. Las respuestas a tus clientes nunca tienen tope.
        {!windowOpen && health.nextCampaignWindow && ` Fuera de horario de campañas: vuelven el ${fmtWindow(health.nextCampaignWindow)}`}
      </p>

      {health.campaignsPaused && (
        <p className="text-sm text-orange-600 bg-orange-50 rounded-xl px-4 py-3 mt-4">
          Campañas en pausa para proteger tu número{health.campaignsPaused.reason ? `: ${health.campaignsPaused.reason}` : ''}. Puedes reanudarlas desde Campañas.
        </p>
      )}
    </div>
  );
}

export default function WhatsAppPage() {
  const { storeId } = useAuth();
  const [connected, setConnected] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [pausedReason, setPausedReason] = useState<string | null>(null);
  const [unlinkHint, setUnlinkHint] = useState<string | null>(null);
  // Lo que contestó la API al conectar/desconectar (p. ej. el límite de 6 cada 10 min).
  const [actionError, setActionError] = useState<string | null>(null);
  const [health, setHealth] = useState<WhatsAppHealth | null>(null);

  // Salud del número: cada 30 s basta (cambia despacio; el QR sí va cada 3 s).
  useEffect(() => {
    const load = () => getWhatsAppHealth(storeId).then((r) => setHealth(r.data)).catch(() => {});
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [storeId]);

  const checkStatus = useCallback(async () => {
    try {
      const res = await getWhatsAppStatus(storeId);
      setConnected(res.data.connected);
      setStatus(res.data.status ?? null);
      setPausedReason(res.data.pausedReason ?? null);
      if (res.data.connected) setQr(null);
    } catch {}
  }, [storeId]);

  const fetchQR = useCallback(async () => {
    try {
      const res = await getWhatsAppQR(storeId);
      if (res.data.qr) setQr(res.data.qr);
    } catch {}
  }, [storeId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await checkStatus();
      setLoading(false);
    };
    init();
  }, [checkStatus]);

  // Poll QR every 3s while not connected
  useEffect(() => {
    if (connected || loading) return;
    fetchQR();
    const interval = setInterval(async () => {
      await checkStatus();
      await fetchQR();
    }, 3000);
    return () => clearInterval(interval);
  }, [connected, loading, checkStatus, fetchQR]);

  const handleConnect = async () => {
    setConnecting(true);
    setUnlinkHint(null);
    setActionError(null);
    try {
      await connectWhatsApp(storeId);
      await fetchQR();
    } catch (err) {
      setActionError(apiMessage(err, 'No se pudo conectar. Intenta de nuevo.'));
    } finally {
      setConnecting(false);
    }
  };

  // Solo tras confirmar: desvincula el teléfono (el único botón que lo hace).
  const handleUnlink = async () => {
    setConfirmUnlink(false);
    setDisconnecting(true);
    setActionError(null);
    try {
      const res = await disconnectWhatsApp(storeId, { logout: true });
      setUnlinkHint(res.data?.hint ?? null);
      setConnected(false);
      setQr(null);
      // Esperar que el backend limpie la sesión antes de re-verificar
      await new Promise(r => setTimeout(r, 1500));
      await checkStatus();
    } catch (err) {
      setActionError(apiMessage(err, 'No se pudo desvincular. Intenta de nuevo.'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCancel = async () => {
    setDisconnecting(true);
    setActionError(null);
    try {
      // Cierra el intento sin desvincular: limpia QR/socket/reintentos en el backend para que
      // el QR no reaparezca con el polling
      await disconnectWhatsApp(storeId);
    } catch (err) {
      setActionError(apiMessage(err, 'No se pudo cancelar. Intenta de nuevo.'));
    } finally {
      setQr(null);
      setDisconnecting(false);
    }
  };

  const handleReconnect = async () => {
    setDisconnecting(true);
    setActionError(null);
    try {
      // Cierra y vuelve a abrir con la misma vinculación: no hace falta escanear otro QR
      await disconnectWhatsApp(storeId);
      setConnected(false);
      setQr(null);
      await new Promise(r => setTimeout(r, 1500));
      await connectWhatsApp(storeId);
      await fetchQR();
    } catch (err) {
      setActionError(apiMessage(err, 'No se pudo reconectar. Intenta de nuevo.'));
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-screen">
        <div className="flex items-center gap-3 text-txt-secondary">
          <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 11-6.219-8.56"/>
          </svg>
          Verificando estado...
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-txt-primary mb-1">WhatsApp</h1>
      <p className="text-txt-secondary mb-8">Conecta tu número de WhatsApp para activar el bot</p>

      <div className="bg-surface rounded-2xl border border-border-subtle shadow-sm p-8 flex flex-col items-center gap-6">

        {/* Estado */}
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
          connected ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-500'
        }`}>
          <span className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-orange-400'} animate-pulse`}/>
          {connected ? 'Conectado' : 'Desconectado'}
        </div>

        {actionError && (
          <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3 text-center">
            {actionError}
          </p>
        )}

        {connected ? (
          <>
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.22 1.18 2 2 0 012.18 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.56-.56a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
              </div>
              <p className="text-txt-primary font-medium">Tu WhatsApp está activo</p>
              <p className="text-txt-tertiary text-sm">El bot está respondiendo mensajes automáticamente</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleReconnect}
                disabled={disconnecting}
                className="px-5 py-2.5 rounded-xl border border-border-default text-txt-secondary text-sm font-semibold hover:bg-surface-elevated transition disabled:opacity-50"
              >
                {disconnecting ? 'Procesando...' : '🔄 Reconectar'}
              </button>
              <button
                onClick={() => setConfirmUnlink(true)}
                disabled={disconnecting}
                className="px-5 py-2.5 rounded-xl border border-red-200 text-red-500 text-sm font-semibold hover:bg-red-50 transition disabled:opacity-50"
              >
                {disconnecting ? 'Desvinculando...' : 'Desvincular este teléfono'}
              </button>
            </div>
          </>
        ) : (
          <>
            {!qr ? (
              <>
                {status === 'blocked' && (
                  <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3 text-center">
                    {pausedReason ?? 'Tu suscripción está vencida. Renueva el plan para reactivar WhatsApp.'}
                  </p>
                )}
                {status === 'paused_risk' && (
                  <p className="text-sm text-orange-600 bg-orange-50 rounded-xl px-4 py-3 text-center">
                    Conexión en pausa para proteger tu número{pausedReason ? `: ${pausedReason}` : ''}. Revisa el teléfono y pulsa Reconectar cuando esté bien.
                  </p>
                )}
                {unlinkHint && (
                  <p className="text-sm text-txt-secondary bg-surface-elevated rounded-xl px-4 py-3 text-center">
                    {unlinkHint}
                  </p>
                )}
                <div className="flex flex-col items-center gap-2 text-center">
                  <div className="w-20 h-20 rounded-full bg-surface-elevated flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.22 1.18 2 2 0 012.18 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.09a16 16 0 006 6l.56-.56a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                  </div>
                  <p className="text-txt-primary font-medium">WhatsApp no conectado</p>
                  <p className="text-txt-tertiary text-sm">Haz clic en conectar para generar el código QR</p>
                </div>

                {status !== 'blocked' && (
                  <button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="px-6 py-3 rounded-xl text-[#0A0A0F] text-sm font-semibold transition disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #D4FF00, #A3CC00)' }}
                  >
                    {connecting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <path d="M21 12a9 9 0 11-6.219-8.56"/>
                        </svg>
                        Generando QR...
                      </span>
                    ) : status === 'paused_risk' ? 'Reconectar' : 'Conectar WhatsApp'}
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-txt-primary font-medium mb-1">Escanea este código QR</p>
                  <p className="text-txt-tertiary text-sm">Abre WhatsApp → Dispositivos vinculados → Vincular dispositivo</p>
                </div>

                <div className="bg-surface border-2 border-border-subtle rounded-xl p-4 flex items-center justify-center">
                  <QRCodeCanvas value={qr} size={240} />
                </div>

                <div className="flex items-center gap-2 text-txt-tertiary text-xs">
                  <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>
                  Actualizando automáticamente...
                </div>

                <button
                  onClick={handleCancel}
                  disabled={disconnecting}
                  className="px-5 py-2.5 rounded-xl border border-border-default text-txt-secondary text-sm font-semibold hover:bg-surface-elevated transition disabled:opacity-50"
                >
                  {disconnecting ? 'Cancelando...' : 'Cancelar'}
                </button>
              </>
            )}
          </>
        )}
      </div>

      {health && <HealthPanel health={health} />}

      {/* Confirmar desvincular */}
      {confirmUnlink && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4">
          <div className="bg-surface rounded-2xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="font-bold text-txt-primary mb-2">Desvincular este teléfono</h3>
            <p className="text-sm text-txt-secondary mb-5">
              Vas a desvincular este teléfono. El bot dejará de responder y tendrás que escanear un QR nuevo para volver a conectarlo.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmUnlink(false)}
                className="flex-1 py-2 rounded-xl text-sm font-medium bg-surface-overlay text-txt-secondary hover:bg-border-default transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleUnlink}
                disabled={disconnecting}
                className="flex-1 py-2 rounded-xl text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 transition"
              >
                Desvincular
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
